import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AreaChart,
    Area,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { BarChart3, Users, Activity, TrendingUp, UserPlus, UserX } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Skeleton } from '../ui/Skeleton';
import { gymDarkContentPadding, gymDarkHeaderBar } from './gymPageTheme';

const COLORS = {
    attivi: '#10b981',
    inRallentamento: '#f59e0b',
    rischio: '#ef4444',
    inattivi: '#6b7280'
};

/** Recharts usa SVG: il testo legenda/assi va con fill esplicito, non solo className Tailwind */
function chartAxisTickProps(isDark) {
    return { fontSize: 12, fill: isDark ? '#dcd6f7' : '#4b5563' };
}

function GymChartLegendContent({ payload, isDark }) {
    if (!payload?.length) return null;
    const textColor = isDark ? '#e8e4ff' : '#374151';
    return (
        <ul
            style={{
                listStyle: 'none',
                margin: 0,
                padding: '8px 0 0',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px 18px',
                fontSize: 12,
                lineHeight: '18px',
                color: textColor,
            }}
        >
            {payload.map((entry, i) => (
                <li
                    key={String(entry.dataKey ?? entry.value ?? i)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                    <span
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: 2,
                            backgroundColor: entry.color,
                            flexShrink: 0,
                        }}
                    />
                    <span>{entry.value}</span>
                </li>
            ))}
        </ul>
    );
}

function GymStatisticsPageShell({ isDark, titleBlock, children }) {
    if (!isDark) {
        return (
            <div className="p-fluid-md">
                {titleBlock}
                {children}
            </div>
        );
    }
    return (
        <div className="box-border flex min-h-full w-full min-w-0 flex-col bg-gray-950">
            <header className={`w-full shrink-0 pt-fluid-md pb-5 ${gymDarkHeaderBar}`}>
                <div className="px-fluid-lg sm:px-fluid-xl">{titleBlock}</div>
            </header>
            <div className={`${gymDarkContentPadding} flex-1 pt-fluid-md`}>{children}</div>
        </div>
    );
}

export default function GymStatistics() {
    const { t } = useTranslation();
    const { isDark } = useTheme();
    const { getAuthenticatedApi } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const api = getAuthenticatedApi();
            const res = await api.get('/gym_statistics_api.php?action=snapshot_monthly&months=6');
            if (res.data?.success && Array.isArray(res.data.data)) {
                setData(res.data.data);
            } else {
                setData([]);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || t('gym.statistics.errorLoad'));
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const axisTick = chartAxisTickProps(isDark);

    const titleBlock = (
        <div className="mb-0 flex items-center gap-3">
            <div className="rounded-lg bg-indigo-100 p-2 dark:bg-gray-800">
                <BarChart3 className="h-8 w-8 text-indigo-600 dark:text-gray-300" />
            </div>
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white dark:font-open-sans">
                    {t('gym.navigation.statisticsGym')}
                </h1>
                <p className="mt-0.5 text-sm text-gray-500 dark:text-white/70">
                    {t('gym.statistics.gymDesc')}
                </p>
            </div>
        </div>
    );

    if (loading) {
        return (
            <GymStatisticsPageShell
                isDark={isDark}
                titleBlock={<HeaderSkeleton />}
            >
                <div className="mt-6 space-y-6">
                    <ChartSkeleton />
                    <ChartSkeleton />
                    <ChartSkeleton />
                    <ChartSkeleton />
                    <ChartSkeleton />
                </div>
            </GymStatisticsPageShell>
        );
    }

    if (error) {
        return (
            <GymStatisticsPageShell isDark={isDark} titleBlock={titleBlock}>
                <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-800/60 dark:bg-red-900/20">
                    <p className="mb-4 text-red-700 dark:text-red-300">{error}</p>
                    <button
                        type="button"
                        onClick={loadData}
                        className="rounded-lg bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700"
                    >
                        {t('gym.statistics.retry')}
                    </button>
                </div>
            </GymStatisticsPageShell>
        );
    }

    return (
        <GymStatisticsPageShell isDark={isDark} titleBlock={titleBlock}>
            {data.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center dark:border-white/10 dark:bg-gray-900/60">
                    <BarChart3 className="mx-auto mb-3 h-12 w-12 text-gray-400 dark:text-white/40" />
                    <p className="text-gray-600 dark:text-white/75">{t('gym.statistics.noData')}</p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-white/55">{t('gym.statistics.noDataHint')}</p>
                </div>
            ) : (
                <div className="space-y-8">
                    <ChartCard
                        title={t('gym.statistics.charts.distributionTitle')}
                        icon={<Users className="text-indigo-600 dark:text-gray-300" size={20} />}
                    >
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-white/12" />
                                <XAxis dataKey="month_label" tick={axisTick} />
                                <YAxis tick={axisTick} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: isDark ? '#111827' : '#fff' }}
                                    formatter={(value) => [value ?? 0, null]}
                                    labelFormatter={(label) => label}
                                    labelStyle={{ color: isDark ? '#f5f3ff' : '#111' }}
                                />
                                <Legend content={(props) => <GymChartLegendContent {...props} isDark={isDark} />} />
                                <Area type="monotone" dataKey="clienti_inattivi" name={t('gym.statistics.charts.inattivi')} stackId="1" fill={COLORS.inattivi} stroke={COLORS.inattivi} />
                                <Area type="monotone" dataKey="clienti_rischio" name={t('gym.statistics.charts.rischio')} stackId="1" fill={COLORS.rischio} stroke={COLORS.rischio} />
                                <Area type="monotone" dataKey="clienti_in_rallentamento" name={t('gym.statistics.charts.inRallentamento')} stackId="1" fill={COLORS.inRallentamento} stroke={COLORS.inRallentamento} />
                                <Area type="monotone" dataKey="clienti_attivi" name={t('gym.statistics.charts.attivi')} stackId="1" fill={COLORS.attivi} stroke={COLORS.attivi} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard
                        title={t('gym.statistics.charts.frequencyTitle')}
                        icon={<Activity className="text-blue-600 dark:text-blue-300" size={20} />}
                    >
                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-white/12" />
                                <XAxis dataKey="month_label" tick={axisTick} />
                                <YAxis tick={axisTick} allowDecimals={true} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: isDark ? '#111827' : '#fff' }}
                                    formatter={(value) => [value != null ? Number(value).toFixed(2) : '-', t('gym.statistics.charts.frequencyLabel')]}
                                    labelStyle={{ color: isDark ? '#f5f3ff' : '#111' }}
                                />
                                <Line type="monotone" dataKey="frequenza_media_settimanale" name={t('gym.statistics.charts.frequencyLabel')} stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard
                        title={t('gym.statistics.charts.continuityTitle')}
                        icon={<TrendingUp className="text-emerald-600 dark:text-emerald-400" size={20} />}
                    >
                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-white/12" />
                                <XAxis dataKey="month_label" tick={axisTick} />
                                <YAxis tick={axisTick} domain={[0, 100]} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: isDark ? '#111827' : '#fff' }}
                                    formatter={(value) => [value != null ? `${Number(value).toFixed(0)}%` : '-', t('gym.statistics.charts.continuityLabel')]}
                                    labelStyle={{ color: isDark ? '#f5f3ff' : '#111' }}
                                />
                                <Line type="monotone" dataKey="indice_continuita_attivita" name={t('gym.statistics.charts.continuityLabel')} stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard
                        title={t('gym.statistics.charts.reactivatedTitle')}
                        icon={<UserPlus className="text-green-600 dark:text-green-400" size={20} />}
                    >
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-white/12" />
                                <XAxis dataKey="month_label" tick={axisTick} />
                                <YAxis tick={axisTick} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: isDark ? '#111827' : '#fff' }}
                                    formatter={(value) => [value ?? 0, t('gym.statistics.charts.reactivatedLabel')]}
                                    labelStyle={{ color: isDark ? '#f5f3ff' : '#111' }}
                                />
                                <Bar dataKey="clienti_riattivati_mese" name={t('gym.statistics.charts.reactivatedLabel')} fill="#22c55e" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard
                        title={t('gym.statistics.charts.inactiveTitle')}
                        icon={<UserX className="text-gray-600 dark:text-white/60" size={20} />}
                    >
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-white/12" />
                                <XAxis dataKey="month_label" tick={axisTick} />
                                <YAxis tick={axisTick} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: isDark ? '#111827' : '#fff' }}
                                    formatter={(value) => [value ?? 0, t('gym.statistics.charts.inactiveLabel')]}
                                    labelStyle={{ color: isDark ? '#f5f3ff' : '#111' }}
                                />
                                <Bar dataKey="clienti_inattivi" name={t('gym.statistics.charts.inactiveLabel')} fill="#6b7280" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            )}
        </GymStatisticsPageShell>
    );
}

function ChartCard({ title, icon, children }) {
    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow dark:border-white/10 dark:bg-gray-900">
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-white/10">
                <div className="rounded-lg bg-gray-100 p-1.5 dark:bg-gray-800">{icon}</div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function HeaderSkeleton() {
    return (
        <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 shrink-0 rounded-lg" />
            <div className="space-y-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-64" />
            </div>
        </div>
    );
}

function ChartSkeleton() {
    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow dark:border-white/10 dark:bg-gray-900">
            <div className="border-b border-gray-100 px-4 py-3 dark:border-white/10">
                <Skeleton className="h-5 w-40" />
            </div>
            <div className="p-4">
                <Skeleton className="h-[280px] w-full rounded" />
            </div>
        </div>
    );
}
